import { useCallback, useRef, useState } from "react";

interface UseFilePickerOptions {
  accept?: string;
}

export function useFilePicker(options: UseFilePickerOptions) {
  const { accept } = options;

  const [files, setFiles] = useState<File[]>([]);

  const openFilePicker = useCallback(() => {
    const input: HTMLInputElement = document.createElement("input");
    input.type = "file";

    if (accept) {
      input.accept = accept;
    }

    input.onchange = () => {
      setFiles(Array.from(input.files ?? []));
    };

    input.click();
  }, [accept]);

  return { files, openFilePicker };
}

export function useFileInput(
  options: UseFilePickerOptions & {
    onFilesSelected: (files: File[]) => void;
  }
) {
  const { accept, onFilesSelected } = options;
  const inputRef = useRef<HTMLInputElement>(null);
  const isHandlingChangeRef = useRef(false);

  const openFilePicker = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const inputElement = (
    <input
      ref={inputRef}
      type="file"
      accept={accept}
      style={{ display: "none" }}
      onChange={(event) => {
        // Guard against the change event firing more than once for a single
        // selection (e.g. duplicate events from some automation tools).
        if (isHandlingChangeRef.current) return;
        isHandlingChangeRef.current = true;

        const files = Array.from(event.target.files ?? []);
        event.target.value = "";
        isHandlingChangeRef.current = false;

        if (files.length) {
          onFilesSelected(files);
        }
      }}
    />
  );

  return { openFilePicker, inputElement };
}
